import { useState, useEffect, useRef } from 'react';
import { usePark } from '../contexts/ParkContext';
import axios from 'axios';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Calculator,
  CheckCircle,
  AlertCircle,
  Zap,
  Download,
  Upload,
  CalendarDays,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Presupuesto = () => {
  const { selectedPark, selectedYear } = usePark();
  const [budget, setBudget] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [generating, setGenerating] = useState(false);
  const fileInputRef = useRef(null);
  const [configCategories, setConfigCategories] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, lineId: null });

  // State for month selection modal
  const [showMonthModal, setShowMonthModal] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);

  // State for extra fees modal
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [extraLines, setExtraLines] = useState([]);
  const [generatingExtra, setGeneratingExtra] = useState(false);
  const [extraForm, setExtraForm] = useState({
    budget_line_id: '',
    num_pagos: '1',
    periodo: 'mensual',
    mes_inicio: '1'
  });

  const MONTHS = [
    { num: 1, name: 'Enero' },
    { num: 2, name: 'Febrero' },
    { num: 3, name: 'Marzo' },
    { num: 4, name: 'Abril' },
    { num: 5, name: 'Mayo' },
    { num: 6, name: 'Junio' },
    { num: 7, name: 'Julio' },
    { num: 8, name: 'Agosto' },
    { num: 9, name: 'Septiembre' },
    { num: 10, name: 'Octubre' },
    { num: 11, name: 'Noviembre' },
    { num: 12, name: 'Diciembre' }
  ];

  const [lineForm, setLineForm] = useState({
    categoria: '',
    subcategoria: '',
    importe_anual: '',
    comentario: '',
    tipo_cuota: 'ordinario'
  });

  // Get subcategories for selected category
  const getSubcategories = () => {
    const cat = configCategories.find(c => c.nombre === lineForm.categoria);
    return cat?.subcategories || [];
  };

  useEffect(() => {
    fetchConfigCategories();
  }, []);

  useEffect(() => {
    if (selectedPark) {
      fetchData();
    }
  }, [selectedPark, selectedYear]);

  const fetchConfigCategories = async () => {
    try {
      const response = await axios.get(`${API}/config/budget-categories`);
      setConfigCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const budgetsRes = await axios.get(`${API}/budgets?park_id=${selectedPark.id}`);
      const currentBudget = budgetsRes.data.find(b => b.año === selectedYear);
      
      if (currentBudget) {
        setBudget(currentBudget);
        const linesRes = await axios.get(`${API}/budget-lines?budget_header_id=${currentBudget.id}`);
        setLines(linesRes.data);
      } else {
        setBudget(null);
        setLines([]);
      }
    } catch (error) {
      console.error('Error fetching budget:', error);
      toast.error('Error al cargar el presupuesto');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBudget = async () => {
    try {
      await axios.post(`${API}/budgets`, {
        park_id: selectedPark.id,
        año: selectedYear,
        estado: 'borrador'
      });
      toast.success('Presupuesto creado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el presupuesto');
    }
  };

  const handleSaveLine = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...lineForm,
        importe_anual: parseFloat(lineForm.importe_anual) || 0,
        budget_header_id: budget.id
      };

      if (editingLine) {
        await axios.put(`${API}/budget-lines/${editingLine.id}`, data);
        toast.success('Línea actualizada');
      } else {
        await axios.post(`${API}/budget-lines`, data);
        toast.success('Línea creada');
      }
      
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar la línea');
    }
  };

  const handleDeleteLine = async (lineId) => {
    try {
      await axios.delete(`${API}/budget-lines/${lineId}`);
      toast.success('Línea eliminada');
      setDeleteConfirm({ open: false, lineId: null });
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar la línea');
    }
  };

  const handleEditLine = (line) => {
    setEditingLine(line);
    setLineForm({
      categoria: line.categoria,
      subcategoria: line.subcategoria || '',
      importe_anual: line.importe_anual.toString(),
      comentario: line.comentario || '',
      tipo_cuota: line.tipo_cuota || 'ordinario'
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingLine(null);
    setLineForm({
      categoria: '',
      subcategoria: '',
      importe_anual: '',
      comentario: '',
      tipo_cuota: 'ordinario'
    });
  };

  const handleGenerateFees = async () => {
    if (selectedMonths.length === 0) {
      toast.error('Seleccione al menos un mes');
      return;
    }
    
    setGenerating(true);
    setShowMonthModal(false);
    try {
      const monthsParam = selectedMonths.join(',');
      const response = await axios.post(`${API}/maintenance-fees/generate?park_id=${selectedPark.id}&año=${selectedYear}&months=${monthsParam}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar las cuotas');
    } finally {
      setGenerating(false);
    }
  };

  const openExtraFeesModal = async () => {
    try {
      const res = await axios.get(`${API}/budget-lines/extraordinary?park_id=${selectedPark.id}&año=${selectedYear}`);
      setExtraLines(res.data);
      if (res.data.length === 0) {
        toast.error('No hay conceptos extraordinarios en el presupuesto');
        return;
      }
      setExtraForm({ budget_line_id: res.data[0]?.id || '', num_pagos: '1', periodo: 'mensual', mes_inicio: '1' });
      setShowExtraModal(true);
    } catch (error) {
      toast.error('Error al cargar conceptos extraordinarios');
    }
  };

  const handleGenerateExtraFees = async () => {
    if (!extraForm.budget_line_id) {
      toast.error('Seleccione un concepto');
      return;
    }
    setGeneratingExtra(true);
    try {
      const response = await axios.post(`${API}/maintenance-fees/generate-extra`, {
        park_id: selectedPark.id,
        año: selectedYear,
        budget_line_id: extraForm.budget_line_id,
        num_pagos: parseInt(extraForm.num_pagos) || 1,
        periodo: extraForm.periodo,
        mes_inicio: parseInt(extraForm.mes_inicio) || 1
      });
      toast.success(response.data.message);
      setShowExtraModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar cuotas extras');
    } finally {
      setGeneratingExtra(false);
    }
  };

  const getExtraPreview = () => {
    const line = extraLines.find(l => l.id === extraForm.budget_line_id);
    if (!line) return null;
    const numPagos = parseInt(extraForm.num_pagos) || 1;
    const importePorPago = line.importe_anual / numPagos;
    const periodoMap = { mensual: 1, bimestral: 2, semestral: 6 };
    const step = periodoMap[extraForm.periodo] || 1;
    const mesInicio = parseInt(extraForm.mes_inicio) || 1;
    const months = [];
    for (let i = 0; i < numPagos; i++) {
      let m = mesInicio + (i * step);
      if (m > 12) m = ((m - 1) % 12) + 1;
      months.push(m);
    }
    return { line, importePorPago, months };
  };

  const openMonthSelector = () => {
    // Reset to all months selected by default
    setSelectedMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
    setShowMonthModal(true);
  };

  const toggleMonth = (monthNum) => {
    setSelectedMonths(prev => 
      prev.includes(monthNum) 
        ? prev.filter(m => m !== monthNum)
        : [...prev, monthNum].sort((a, b) => a - b)
    );
  };

  const selectAllMonths = () => {
    setSelectedMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  };

  const selectNoMonths = () => {
    setSelectedMonths([]);
  };

  const selectFirstSemester = () => {
    setSelectedMonths([1, 2, 3, 4, 5, 6]);
  };

  const selectSecondSemester = () => {
    setSelectedMonths([7, 8, 9, 10, 11, 12]);
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await axios.put(`${API}/budgets/${budget.id}`, {
        ...budget,
        estado: newStatus
      });
      toast.success(`Presupuesto ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el estado');
    }
  };

  const downloadCSVTemplate = () => {
    const template = `categoria,subcategoria,importe_anual,comentario
Seguridad,Vigilancia 24hrs,240000,3 guardias por turno
Seguridad,CCTV,36000,Mantenimiento cámaras
Mantenimiento,Áreas comunes,120000,Pintura y reparaciones
Administración,Sueldos,180000,Personal administrativo
Jardinería,Mantenimiento,48000,Podas y riego
Limpieza,Áreas comunes,72000,Limpieza diaria
Servicios,Energía eléctrica,96000,Alumbrado exterior
Servicios,Agua,24000,Riego y sanitarios
Seguros,Póliza general,60000,Cobertura anual
Otros,Imprevistos,50000,Reserva para emergencias`;
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_presupuesto.csv';
    a.click();
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!budget) {
      toast.error('Primero debes crear un presupuesto para el año');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/budget-lines/import-csv?budget_header_id=${budget.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Se importaron ${response.data.imported} líneas de presupuesto`);
      if (response.data.errors.length > 0) {
        response.data.errors.slice(0, 5).forEach(err => toast.error(err));
        if (response.data.errors.length > 5) {
          toast.error(`...y ${response.data.errors.length - 5} errores más`);
        }
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
    }
    
    e.target.value = '';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value || 0);
  };

  const totalPresupuesto = lines.reduce((sum, l) => sum + (l.importe_anual || 0), 0);
  const cuotaMensualTotal = totalPresupuesto / 12;
  const totalOrdinario = lines.filter(l => (l.tipo_cuota || 'ordinario') === 'ordinario').reduce((sum, l) => sum + (l.importe_anual || 0), 0);
  const totalExtraordinario = lines.filter(l => l.tipo_cuota === 'extraordinario').reduce((sum, l) => sum + (l.importe_anual || 0), 0);

  if (!selectedPark) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Presupuesto Operativo</h1>
          <p>Selecciona un parque para ver el presupuesto</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="presupuesto-title">Presupuesto Operativo</h1>
        <p>{selectedPark?.nombre} - Año {selectedYear}</p>
      </div>

      {/* Budget Header */}
      {!loading && !budget ? (
        <div className="empty-state py-24 card-industrial">
          <div className="empty-state-icon bg-amber-100">
            <Calculator size={32} className="text-amber-600" />
          </div>
          <h3>No hay presupuesto para {selectedYear}</h3>
          <p className="mb-6">Crea un presupuesto para comenzar a registrar las líneas de gasto.</p>
          <Button 
            className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
            onClick={handleCreateBudget}
            data-testid="create-budget-btn"
          >
            <Plus size={16} className="mr-2" />
            Crear Presupuesto {selectedYear}
          </Button>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="card-industrial">
              <span className="label-overline">Presupuesto Anual</span>
              <p className="text-3xl font-black tracking-tight mt-2 tabular-nums" data-testid="total-presupuesto">
                {formatCurrency(totalPresupuesto)}
              </p>
            </div>
            <div className="card-industrial">
              <span className="label-overline">Cuota Mensual Total</span>
              <p className="text-3xl font-black tracking-tight mt-2 tabular-nums">
                {formatCurrency(cuotaMensualTotal)}
              </p>
            </div>
            <div className="card-industrial">
              <span className="label-overline">Estado</span>
              <div className="flex items-center gap-3 mt-2">
                <span className={`inline-flex items-center gap-2 px-3 py-1 text-sm font-medium capitalize
                  ${budget?.estado === 'aprobado' ? 'bg-emerald-100 text-emerald-800' : ''}
                  ${budget?.estado === 'borrador' ? 'bg-amber-100 text-amber-800' : ''}
                  ${budget?.estado === 'cerrado' ? 'bg-slate-100 text-slate-800' : ''}
                `}>
                  {budget?.estado === 'aprobado' && <CheckCircle size={16} />}
                  {budget?.estado === 'borrador' && <AlertCircle size={16} />}
                  {budget?.estado}
                </span>
                {budget?.estado === 'borrador' && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleUpdateStatus('aprobado')}
                    className="rounded-none text-xs"
                    data-testid="approve-budget-btn"
                  >
                    Aprobar
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="action-bar">
            <span className="text-slate-500">{lines.length} líneas de presupuesto</span>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="rounded-none"
                onClick={downloadCSVTemplate}
                data-testid="download-budget-template-btn"
              >
                <Download size={16} className="mr-2" />
                Plantilla CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
              />
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => fileInputRef.current?.click()}
                disabled={!budget || budget?.estado === 'cerrado'}
                data-testid="import-budget-csv-btn"
              >
                <Upload size={16} className="mr-2" />
                Importar CSV
              </Button>
              {budget?.estado === 'aprobado' && (
                <>
                  <Button
                    className="btn-industrial bg-amber-500 text-slate-900 hover:bg-amber-400"
                    onClick={openMonthSelector}
                    disabled={generating}
                    data-testid="generate-fees-btn"
                  >
                    <Zap size={16} className="mr-2" />
                    {generating ? 'Generando...' : 'Generar Cuotas del Año'}
                  </Button>
                  <Button
                    className="btn-industrial bg-amber-700 text-white hover:bg-amber-600"
                    onClick={openExtraFeesModal}
                    disabled={generatingExtra}
                    data-testid="generate-extra-fees-btn"
                  >
                    <Star size={16} className="mr-2" />
                    {generatingExtra ? 'Generando...' : 'Generar Cuotas Extras'}
                  </Button>
                </>
              )}
              <Button 
                className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
                onClick={() => { resetForm(); setShowModal(true); }}
                disabled={budget?.estado === 'cerrado'}
                data-testid="add-line-btn"
              >
                <Plus size={16} className="mr-2" />
                Nueva Línea
              </Button>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="card-industrial animate-pulse">
              <div className="h-64 bg-slate-100"></div>
            </div>
          ) : lines.length === 0 ? (
            <div className="empty-state py-16 card-industrial">
              <div className="empty-state-icon">
                <Calculator size={24} className="text-slate-400" />
              </div>
              <h3>No hay líneas de presupuesto</h3>
              <p>Agrega las categorías de gasto para el año.</p>
            </div>
          ) : (
            <div className="card-industrial overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="table-blueprint" data-testid="budget-lines-table">
                  <thead>
                    <tr>
                      <th>Categoría</th>
                      <th>Subcategoría</th>
                      <th>Tipo</th>
                      <th className="text-right">Importe Anual</th>
                      <th className="text-right">Mensual</th>
                      <th>Comentario</th>
                      <th className="w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, index) => (
                      <tr key={line.id} className="animate-slideIn" style={{ animationDelay: `${index * 30}ms` }}>
                        <td className="font-medium">{line.categoria}</td>
                        <td className="text-slate-500">{line.subcategoria || '-'}</td>
                        <td>
                          <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                            line.tipo_cuota === 'extraordinario' 
                              ? 'bg-amber-100 text-amber-800' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {line.tipo_cuota === 'extraordinario' ? 'Extra' : 'Ordinario'}
                          </span>
                        </td>
                        <td className="text-right tabular-nums font-medium">{formatCurrency(line.importe_anual)}</td>
                        <td className="text-right tabular-nums text-slate-500">{formatCurrency(line.importe_anual / 12)}</td>
                        <td className="text-slate-500 text-sm truncate max-w-xs">{line.comentario || '-'}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditLine(line)}
                              className="p-2 hover:bg-slate-100 transition-colors"
                              disabled={budget?.estado === 'cerrado'}
                              data-testid={`edit-line-${line.categoria}`}
                            >
                              <Edit2 size={16} className="text-slate-500" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm({ open: true, lineId: line.id })}
                              className="p-2 hover:bg-red-50 transition-colors"
                              disabled={budget?.estado === 'cerrado'}
                              data-testid={`delete-line-${line.categoria}`}
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-medium">
                      <td colSpan={2}>Total Ordinario</td>
                      <td></td>
                      <td className="text-right tabular-nums">{formatCurrency(totalOrdinario)}</td>
                      <td className="text-right tabular-nums">{formatCurrency(totalOrdinario / 12)}</td>
                      <td colSpan={2}></td>
                    </tr>
                    {totalExtraordinario > 0 && (
                      <tr className="bg-amber-50 font-medium">
                        <td colSpan={2}>Total Extraordinario</td>
                        <td></td>
                        <td className="text-right tabular-nums text-amber-700">{formatCurrency(totalExtraordinario)}</td>
                        <td className="text-right tabular-nums text-amber-600">{formatCurrency(totalExtraordinario / 12)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    )}
                    <tr className="bg-slate-100 font-bold">
                      <td colSpan={2}>Total General</td>
                      <td></td>
                      <td className="text-right tabular-nums">{formatCurrency(totalPresupuesto)}</td>
                      <td className="text-right tabular-nums">{formatCurrency(cuotaMensualTotal)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Line Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">
              {editingLine ? 'Editar Línea' : 'Nueva Línea de Presupuesto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveLine}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Categoría</Label>
                <Select
                  value={lineForm.categoria}
                  onValueChange={(value) => setLineForm({ ...lineForm, categoria: value, subcategoria: '' })}
                >
                  <SelectTrigger className="input-industrial" data-testid="line-categoria-select">
                    <SelectValue placeholder="Seleccionar categoría..." />
                  </SelectTrigger>
                  <SelectContent>
                    {configCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.nombre}>{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Subcategoría</Label>
                <Select
                  value={lineForm.subcategoria || "none"}
                  onValueChange={(value) => setLineForm({ ...lineForm, subcategoria: value === "none" ? "" : value })}
                  disabled={!lineForm.categoria}
                >
                  <SelectTrigger className="input-industrial" data-testid="line-subcategoria-select">
                    <SelectValue placeholder="Seleccionar subcategoría..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin subcategoría</SelectItem>
                    {getSubcategories().map(sub => (
                      <SelectItem key={sub.id} value={sub.nombre}>{sub.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {lineForm.categoria && getSubcategories().length === 0 && (
                  <p className="text-xs text-amber-600">No hay subcategorías configuradas para esta categoría</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Importe Anual (MXN)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={lineForm.importe_anual}
                  onChange={(e) => setLineForm({ ...lineForm, importe_anual: e.target.value })}
                  placeholder="100000"
                  required
                  className="input-industrial"
                  data-testid="line-importe-input"
                />
                {lineForm.importe_anual && (
                  <p className="text-sm text-slate-500">
                    Mensual: {formatCurrency(parseFloat(lineForm.importe_anual) / 12)}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Comentario (opcional)</Label>
                <Input
                  value={lineForm.comentario}
                  onChange={(e) => setLineForm({ ...lineForm, comentario: e.target.value })}
                  placeholder="Notas adicionales..."
                  className="input-industrial"
                  data-testid="line-comentario-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Tipo de Cuota</Label>
                <Select
                  value={lineForm.tipo_cuota}
                  onValueChange={(value) => setLineForm({ ...lineForm, tipo_cuota: value })}
                >
                  <SelectTrigger className="input-industrial" data-testid="line-tipo-cuota-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ordinario">Ordinario</SelectItem>
                    <SelectItem value="extraordinario">Extraordinario</SelectItem>
                  </SelectContent>
                </Select>
                {lineForm.tipo_cuota === 'extraordinario' && (
                  <p className="text-xs text-amber-600">
                    Este concepto no se incluirá en la generación de cuotas regulares. Use "Generar Cuotas Extras" para generar los adeudos.
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-line-btn">
                {editingLine ? 'Guardar Cambios' : 'Crear Línea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ ...deleteConfirm, open })}
        onConfirm={() => handleDeleteLine(deleteConfirm.lineId)}
        title="¿Estás seguro que deseas eliminar?"
        description="Esta línea de presupuesto será eliminada permanentemente."
        confirmText="Eliminar"
      />

      {/* Month Selection Modal */}
      <Dialog open={showMonthModal} onOpenChange={setShowMonthModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="text-amber-500" size={20} />
              Generar Cuotas - Seleccionar Meses
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Seleccione los meses para los que desea generar cuotas en el año <strong>{selectedYear}</strong>.
              Esto es útil para parques que no operan todo el año.
            </p>

            {/* Quick selection buttons */}
            <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b">
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={selectAllMonths}
                className="text-xs"
              >
                Todo el año
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={selectFirstSemester}
                className="text-xs"
              >
                1er Semestre
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={selectSecondSemester}
                className="text-xs"
              >
                2do Semestre
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={selectNoMonths}
                className="text-xs"
              >
                Ninguno
              </Button>
            </div>

            {/* Month checkboxes */}
            <div className="grid grid-cols-3 gap-3">
              {MONTHS.map((month) => (
                <div 
                  key={month.num}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                    selectedMonths.includes(month.num) 
                      ? 'bg-amber-50 border-amber-300' 
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                  }`}
                  onClick={() => toggleMonth(month.num)}
                >
                  <Checkbox
                    id={`month-${month.num}`}
                    checked={selectedMonths.includes(month.num)}
                    onCheckedChange={() => toggleMonth(month.num)}
                  />
                  <label 
                    htmlFor={`month-${month.num}`}
                    className="text-sm cursor-pointer select-none flex-1"
                  >
                    {month.name}
                  </label>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-slate-600">
                <strong>{selectedMonths.length}</strong> {selectedMonths.length === 1 ? 'mes seleccionado' : 'meses seleccionados'}
                {selectedMonths.length > 0 && selectedMonths.length < 12 && (
                  <span className="text-amber-600 ml-2">
                    (Las cuotas existentes de meses no seleccionados se mantendrán)
                  </span>
                )}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowMonthModal(false)}
              className="rounded-none"
            >
              Cancelar
            </Button>
            <Button 
              type="button" 
              onClick={handleGenerateFees}
              disabled={selectedMonths.length === 0 || generating}
              className="btn-industrial bg-amber-500 text-slate-900 hover:bg-amber-400"
              data-testid="confirm-generate-fees-btn"
            >
              <Zap size={16} className="mr-2" />
              {generating ? 'Generando...' : `Generar ${selectedMonths.length} ${selectedMonths.length === 1 ? 'Mes' : 'Meses'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extra Fees Generation Modal */}
      <Dialog open={showExtraModal} onOpenChange={setShowExtraModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="text-amber-600" size={20} />
              Generar Cuotas Extraordinarias
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Concepto Extraordinario</Label>
              <Select
                value={extraForm.budget_line_id}
                onValueChange={(v) => setExtraForm({ ...extraForm, budget_line_id: v })}
              >
                <SelectTrigger className="input-industrial" data-testid="extra-concepto-select">
                  <SelectValue placeholder="Seleccionar concepto..." />
                </SelectTrigger>
                <SelectContent>
                  {extraLines.map(line => (
                    <SelectItem key={line.id} value={line.id}>
                      {line.categoria}{line.subcategoria ? ` - ${line.subcategoria}` : ''} ({formatCurrency(line.importe_anual)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Número de Pagos</Label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={extraForm.num_pagos}
                  onChange={(e) => setExtraForm({ ...extraForm, num_pagos: e.target.value })}
                  className="input-industrial"
                  data-testid="extra-num-pagos-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Periodo de Cobro</Label>
                <Select
                  value={extraForm.periodo}
                  onValueChange={(v) => setExtraForm({ ...extraForm, periodo: v })}
                >
                  <SelectTrigger className="input-industrial" data-testid="extra-periodo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensual">Mensual</SelectItem>
                    <SelectItem value="bimestral">Bimestral</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">A partir de</Label>
                <Select
                  value={extraForm.mes_inicio}
                  onValueChange={(v) => setExtraForm({ ...extraForm, mes_inicio: v })}
                >
                  <SelectTrigger className="input-industrial" data-testid="extra-mes-inicio-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m.num} value={String(m.num)}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview */}
            {getExtraPreview() && (
              <div className="bg-amber-50 border border-amber-200 rounded p-4 space-y-2">
                <p className="text-sm font-medium text-amber-800">Vista previa:</p>
                <div className="text-sm text-amber-700 space-y-1">
                  <p>Concepto: <strong>{getExtraPreview().line.categoria}{getExtraPreview().line.subcategoria ? ` - ${getExtraPreview().line.subcategoria}` : ''}</strong></p>
                  <p>Importe total: <strong>{formatCurrency(getExtraPreview().line.importe_anual)}</strong></p>
                  <p>Importe por pago: <strong>{formatCurrency(getExtraPreview().importePorPago)}</strong> + IVA</p>
                  <p>Meses de cobro: <strong>{getExtraPreview().months.map(m => MONTHS.find(mo => mo.num === m)?.name).join(', ')}</strong></p>
                  <p className="text-xs mt-2 text-amber-600">Se aplicará por proindiviso a cada lote del parque.</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowExtraModal(false)} className="rounded-none">
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleGenerateExtraFees}
              disabled={!extraForm.budget_line_id || generatingExtra}
              className="btn-industrial bg-amber-700 text-white hover:bg-amber-600"
              data-testid="confirm-generate-extra-btn"
            >
              <Star size={16} className="mr-2" />
              {generatingExtra ? 'Generando...' : 'Generar Cuotas Extras'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Presupuesto;
